import {
  useTheme,
  Box,
  Stack,
  FormControl,
  Select,
  MenuItem,
  Typography,
  alpha,
} from '@mui/material'
import { useState, useRef, useEffect } from 'react'
import { Scatter } from 'react-chartjs-2'
import { Chart as ChartJS, Plugin } from 'chart.js'
import { useTranslation } from 'react-i18next'
import { max, min } from 'lodash'
import { useAppSelector } from '../../store/hooks'
import { roundUpToNearest10 } from '../../shared/utils'
import { fsColors } from '../../shared/constants'

const ChartKPIG = ({ results }: { results: any }) => {
  const theme = useTheme()
  const { t } = useTranslation()
  const { currentLanguage } = useAppSelector((state) => state.currentUser)
  const [curPayComp, setCurPayComp] = useState('basic_wage')
  const [tooltip, setTooltip] = useState({ visible: false, x: 0, y: 0, text: '' })
  const tooltipRef = useRef<HTMLDivElement>(null)
  const [arrowOffset, setArrowOffset] = useState(15) // default to center-ish

  useEffect(() => {
    if (tooltipRef.current) {
      const height = tooltipRef.current.offsetHeight
      setArrowOffset(height - 80)
    }
  }, [tooltip])

  const upperLimit = 40
  const lowerLimit = -40

  const labels = results.map((r: any) => t(r.label.text))
  const values = results.map((r: any) => {
    const curVal = r.value.find((rv: any) => rv.label === curPayComp)
    return curVal ? (curVal.value !== null ? Number(curVal.value) : null) : null
  })
  const clampedValues = results.map((r: any) => {
    const curVal = r.value.find((rv: any) => rv.label === curPayComp)
    const clampedValue = curVal
      ? curVal.value !== null
        ? Number(curVal.value) > upperLimit
          ? upperLimit
          : Number(curVal.value) < lowerLimit
          ? lowerLimit
          : Number(curVal.value)
        : null
      : null
    return { x: clampedValue, y: t(r.label.text) }
  })

  const itemCount = labels.length
  const rowHeight = 60
  const chartHeight = itemCount * rowHeight

  // const minRange = roundUpToNearest10(Number(min(values) ?? 0) - 10)
  // const maxRange = roundUpToNearest10(Number(max(values) ?? 0) + 10)

  const minRange = lowerLimit
  const maxRange = upperLimit

  const annotations: any = {
    lineAtFive: {
      type: 'line',
      scaleID: 'x',
      value: (5 - minRange) / (maxRange - minRange),
      endValue: (5 - minRange) / (maxRange - minRange),
      borderColor: alpha(theme.palette.primary.main, 0.6),
      borderWidth: 2,
      borderDash: [5, 4],
      label: {
        backgroundColor: 'transparent',
        color: theme.palette.primary.main,
        font: {
          size: 14,
          weight: 'bold',
          lineHeight: 1.2,
        },
        display: true,
        content: ['5 %'],
        position: 'start',
        textAlign: 'center',
        xAdjust: 0,
        yAdjust: -53,
      },
    },
    lineAtMinusFive: {
      type: 'line',
      scaleID: 'x',
      value: (-5 - minRange) / (maxRange - minRange),
      endValue: (-5 - minRange) / (maxRange - minRange),
      borderColor: alpha(theme.palette.primary.main, 0.6),
      borderWidth: 2,
      borderDash: [5, 4],
      label: {
        backgroundColor: 'transparent',
        color: theme.palette.primary.main,
        font: {
          size: 14,
          weight: 'bold',
          lineHeight: 1.2,
        },
        display: true,
        content: ['-5 %'],
        position: 'start',
        textAlign: 'center',
        xAdjust: 0,
        yAdjust: -53,
      },
    },
    lineAtZero: {
      type: 'line',
      scaleID: 'x',
      value: (0 - minRange) / (maxRange - minRange),
      endValue: (0 - minRange) / (maxRange - minRange),
      borderColor: alpha(theme.palette.text.secondary, 0.2),
      borderWidth: 2,
    },
  }

  values.forEach((value: any, index: any) => {
    if (value === null) {
      annotations[`lineAtValue${index}`] = {
        type: 'line',
        scaleID: 'y',
        value: index,
        endValue: index,
        borderColor: alpha(theme.palette.text.secondary, 0.05),
        borderWidth: 2,
        label: {
          backgroundColor: 'transparent',
          color: theme.palette.primary.main,
          font: {
            size: 14,
            weight: 'bold',
            lineHeight: 1.2,
          },
          display: true,
          content: ['N/A'],
          position: 'start',
          textAlign: 'center',
          xAdjust: 0,
          yAdjust: 0,
        },
      }
    }
  })

  const axisLabelTooltipPlugin = {
    id: 'yAxisLabelTooltip',
    afterDraw: (chart: any) => {
      const yAxis = chart.scales.y

      if (!yAxis) return

      const ctx = chart.ctx
      const canvas = chart.canvas

      ctx.save()
      ctx.fillStyle = 'black'
      ctx.font = '14px Arial'
      ctx.textBaseline = 'middle'

      // Prepare array to store icon positions for hover detection
      chart.helpIconPositions = []

      yAxis.ticks.forEach((tick: any, index: number) => {
        const y = yAxis.getPixelForTick(index)
        const labelText = tick.label.toString()
        const x = yAxis.left - labelText.length

        const radius = 8

        // Draw circle
        ctx.beginPath()
        ctx.arc(x + radius, y, radius, 0, 2 * Math.PI)
        ctx.fillStyle = 'white' // circle background color
        ctx.fill()
        ctx.strokeStyle = 'gray' // circle border color
        ctx.lineWidth = 1
        ctx.stroke()

        // Draw "?" centered in the circle
        ctx.fillStyle = 'gray' // text color inside circle
        ctx.font = 'bold 11px Arial'
        ctx.textAlign = 'center'
        ctx.textBaseline = 'middle'
        ctx.fillText('?', x + radius, y)

        // Store bounding box for hover detection
        chart.helpIconPositions.push({
          x: x + radius - radius,
          y: y - radius,
          width: radius * 2,
          height: radius * 2,
          label: index,
        })
      })

      ctx.restore()
    },
    afterEvent: (chart: any, args: any) => {
      const event = args.event
      if (event.type !== 'mousemove' && event.type !== 'mouseout') return

      const canvas = chart.canvas

      if (!chart.helpIconPositions) return

      if (event.type === 'mousemove') {
        const rect = canvas.getBoundingClientRect()
        const mouseX = event.x
        const mouseY = event.y
        const hoveredIcon = chart.helpIconPositions.find((icon: any) => {
          return (
            mouseX >= icon.x &&
            mouseX <= icon.x + icon.width &&
            mouseY >= icon.y &&
            mouseY <= icon.y + icon.height
          )
        })
        const shouldShow = !!hoveredIcon
        if (
          shouldShow &&
          (chart._lastTooltipLabel !== hoveredIcon.label ||
            chart._lastTooltipX !== event.x ||
            chart._lastTooltipY !== event.y)
        ) {
          chart._lastTooltipLabel = hoveredIcon.label
          chart._lastTooltipX = event.x + rect.left
          chart._lastTooltipY = event.y + rect.top

          setTooltip({
            visible: true,
            x: event.x + rect.left,
            y: event.y + rect.top,
            text: hoveredIcon.label,
          })
        }
        if (!shouldShow && chart._lastTooltipLabel !== null) {
          chart._lastTooltipLabel = null
          setTooltip({ visible: false, x: 0, y: 0, text: '' })
        }
      }

      if (event.type === 'mouseout') {
        chart._lastTooltipLabel = null
        setTooltip({ visible: false, x: 0, y: 0, text: '' })
      }
    },
  }

  const drawHorizontalLinesPlugin: Plugin<'scatter'> = {
    id: 'horizontalLinesPlugin',
    afterDatasetsDraw(chart) {
      const { ctx, data, scales } = chart

      const xZero = scales.xBottom.getPixelForValue(0)

      ctx.save()
      ctx.lineWidth = 1

      data.datasets.forEach((dataset) => {
        dataset.data.forEach((point) => {
          if (point && typeof point === 'object' && 'x' in point && 'y' in point) {
            const x = scales.xBottom.getPixelForValue(point.x)
            const y = scales.y.getPixelForValue(point.y)
            ctx.strokeStyle =
              point.x >= 0 ? fsColors.fsDarkBlue.colors[1] : fsColors.fsGray.colors[1]

            ctx.beginPath()
            ctx.moveTo(xZero, y)
            ctx.lineTo(x, y)
            ctx.stroke()
          }
        })
      })

      ctx.restore()
    },
  }

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="center" mx={4} mt={4}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="center"
          minWidth={600}
          width="100%"
        >
          <Typography fontWeight="bold">{t('type.pay.comp')}</Typography>
          {results[0].value.length > 1 ? (
            <FormControl sx={{ minWidth: '50%', ml: 2 }} size="small">
              <Select
                value={curPayComp}
                id="pay-component-select"
                label=""
                onChange={(e) => setCurPayComp(e.target.value)}
              >
                {results[0].value.map((rvv: any) => (
                  <MenuItem value={rvv.label} key={`pay-compo-item-${rvv.label}`}>
                    {t(`etrl.kpi.${rvv.label}` as any)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          ) : (
            <Typography ml={0.5}>{`: ${t(`etrl.kpi.${curPayComp}` as any)}`}</Typography>
          )}
        </Stack>
      </Stack>
      <Box py={2} height={chartHeight + 30}>
        <Scatter
          className="cwi_pay_gap"
          id="cwi_pay_gap"
          data={{
            labels,
            datasets: [
              {
                data: clampedValues,
                backgroundColor: (context) => {
                  // @ts-ignore
                  return context.raw?.x >= 0
                    ? fsColors.fsDarkBlue.colors[1]
                    : fsColors.fsGray.colors[1]
                },
                pointStyle: (context) =>
                  values[context.dataIndex] < lowerLimit || values[context.dataIndex] > upperLimit
                    ? 'triangle'
                    : 'circle',
                pointRadius: 8,
                pointHoverRadius: 8,
                xAxisID: 'xBottom',
              },
            ],
          }}
          options={{
            responsive: true,
            maintainAspectRatio: false,
            indexAxis: 'y',
            layout: {
              padding: {
                top: 10,
                right: 80,
                left: 25,
              },
            },
            plugins: {
              // @ts-ignore
              legendMargin: false,
              legend: {
                display: false,
              },
              tooltip: {
                enabled: true,
                callbacks: {
                  title: (context) => `CWI ${context[0].label}`,
                  label: (context) => {
                    return `${t('gender.pay.gap.company')} (${t(
                      'mean.value',
                      // @ts-ignore
                    )}): ${values[context.dataIndex].toLocaleString(currentLanguage, {
                      maximumFractionDigits: 2,
                    })} %`
                  },
                },
              },
              datalabels: {
                anchor: 'end',
                align: 'end',
                color: theme.palette.text.primary,
                formatter: (value, context) => {
                  const isLess = value.x !== null && values[context.dataIndex] < lowerLimit
                  const isMore = value.x !== null && values[context.dataIndex] > upperLimit
                  return value.x !== null
                    ? `${isLess ? '<' : ''}${isMore ? '>' : ''} ${Number(value.x).toLocaleString(
                        currentLanguage,
                        {
                          maximumFractionDigits: 2,
                        },
                      )} % ${
                        isLess || isMore
                          ? `(${Number(values[context.dataIndex]).toLocaleString(currentLanguage, {
                              maximumFractionDigits: 2,
                            })} %)`
                          : ''
                      }`
                    : ''
                },
              },
              annotation: {
                clip: false,
                annotations,
              },
            },
            scales: {
              x: {
                display: false,
              },
              xBottom: {
                offset: true,
                title: {
                  display: false,
                },
                type: 'linear',
                min: minRange,
                max: maxRange,
                grid: {
                  display: true,
                  color: theme.palette.divider,
                },
                border: {
                  color: theme.palette.text.disabled,
                },
                ticks: {
                  autoSkip: true,
                  stepSize: 10,
                  maxTicksLimit: 8,
                  color: theme.palette.text.secondary,
                  callback: (value) =>
                    `${Number(value).toLocaleString(currentLanguage, {
                      maximumFractionDigits: 2,
                    })} %`,
                },
              },
              xTop: {
                offset: true,
                position: 'top',
                type: 'linear',
                min: minRange,
                max: maxRange,
                grid: {
                  display: false,
                },
                border: {
                  display: false,
                },
                ticks: {
                  autoSkip: true,
                  stepSize: 10,
                  maxTicksLimit: 8,
                  color: theme.palette.text.secondary,
                  callback: (value) =>
                    `${Number(value).toLocaleString(currentLanguage, {
                      maximumFractionDigits: 2,
                    })} %`,
                },
              },
              y: {
                title: {
                  display: false,
                  text: t('ofa.header.cwi'),
                },
                grid: {
                  display: false,
                  color: theme.palette.divider,
                },
                border: {
                  color: theme.palette.text.disabled,
                },
                ticks: {
                  color: theme.palette.text.primary,
                  autoSkip: false,
                  padding: 20,
                  font: {
                    weight: 'bold',
                  },
                },
                offset: true,
                type: 'category',
              },
            },
          }}
          plugins={[axisLabelTooltipPlugin, drawHorizontalLinesPlugin]}
        />
        {tooltip.visible && (
          <div
            ref={tooltipRef}
            style={{
              position: 'fixed',
              top: tooltip.y - arrowOffset,
              left: tooltip.x + 5,
              background: 'rgba(0,0,0,0.8)',
              color: 'white',
              padding: '6px 10px',
              borderRadius: 4,
              fontSize: 12,
              whiteSpace: 'nowrap',
              pointerEvents: 'none',
              zIndex: 1000,
              transform: 'translateX(10px)',
            }}
          >
            <Box>
              <Typography fontWeight="bold">{`${t('etrl.kpi.occ.inc')}:`}</Typography>
              {results[tooltip.text].label.tooltipText.map((ob: any) => (
                <Box mt={1.5}>
                  <Typography>{`\r\n\u2022 ${t('pt.assistance.selected.occ.kldb')} ${
                    ob.kldb20105
                  }`}</Typography>
                  <Typography ml={1.5}>{`${
                    currentLanguage === 'de' ? ob.bezeichnungKldb : ob.titleKldb
                  }`}</Typography>
                </Box>
              ))}
            </Box>

            <div
              style={{
                position: 'absolute',
                top: arrowOffset,
                left: '-5px',
                transform: 'translateY(-50%)',
                width: 0,
                height: 0,
                borderTop: '6px solid transparent',
                borderBottom: '6px solid transparent',
                borderRight: '6px solid rgba(0, 0, 0, 0.8)',
              }}
            />
          </div>
        )}
      </Box>
    </Box>
  )
}

export default ChartKPIG
